import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { LeaderboardEntry, LeaderboardRequest } from '@/shared/types';
import type { RootState } from '../index';

interface LeaderboardState {
  byTournamentId: { [tournamentId: number]: LeaderboardEntry[] };
  lastUpdated: { [tournamentId: number]: number }; // Timestamp
  loadingStates: { [tournamentId: number]: boolean };
  latestRequest: LeaderboardRequest | null;
}

const initialState: LeaderboardState = {
  byTournamentId: {},
  lastUpdated: {},
  loadingStates: {},
  latestRequest: null
};

const leaderboardSlice = createSlice({
  name: 'leaderboard',
  initialState,
  reducers: {
    // Leaderboard received (from WebSocket or HTTP)
    leaderboardReceived: (state, action: PayloadAction<LeaderboardRequest & { timestamp?: number }>) => {
      const { tournamentId, leaderboard, timestamp = Date.now() } = action.payload;

      if (!leaderboard) return;

      // Only update if newer (timestamp-based conflict resolution)
      if (!state.lastUpdated[tournamentId] || timestamp >= state.lastUpdated[tournamentId]) {
        state.byTournamentId[tournamentId] = leaderboard;
        state.lastUpdated[tournamentId] = timestamp;
        state.latestRequest = { tournamentId, leaderboard };
      } else {
      }

      state.loadingStates[tournamentId] = false;
    },

    // Set loading state
    setLeaderboardLoading: (state, action: PayloadAction<{ tournamentId: number; loading: boolean }>) => {
      const { tournamentId, loading } = action.payload;
      state.loadingStates[tournamentId] = loading;
    },

    // Clear leaderboard for tournament
    clearLeaderboardForTournament: (state, action: PayloadAction<number>) => {
      const tournamentId = action.payload;
      delete state.byTournamentId[tournamentId];
      delete state.lastUpdated[tournamentId];
      delete state.loadingStates[tournamentId];
    },

    // Clear all leaderboards
    clearAllLeaderboards: (state) => {
      state.byTournamentId = {};
      state.lastUpdated = {};
      state.loadingStates = {};
      state.latestRequest = null;
    },

    clearLatestLeaderboardRequest: (state) => {
      state.latestRequest = null;
    }
  }
});

export const {
  leaderboardReceived,
  setLeaderboardLoading,
  clearLeaderboardForTournament,
  clearAllLeaderboards,
  clearLatestLeaderboardRequest
} = leaderboardSlice.actions;

// Selectors
export const selectLeaderboardForTournament = (state: RootState, tournamentId: number) =>
  state.leaderboard.byTournamentId[tournamentId] || [];

export const selectLeaderboardLoading = (state: RootState, tournamentId: number) =>
  state.leaderboard.loadingStates[tournamentId] || false;

export const selectLeaderboardLastUpdated = (state: RootState, tournamentId: number) =>
  state.leaderboard.lastUpdated[tournamentId];
export const selectLatestLeaderboardRequest = (state: RootState) => state.leaderboard.latestRequest;

export default leaderboardSlice.reducer;
